/*
  File: CustomersPage.tsx
  Path: src/features/dashboard/pages/CustomersPage.tsx
  Description:
    AQUORIX Customers Surface Phase 2.
    Customer List page consuming GET /api/v1/dashboard/customers.
    AQX-C customer ID is deferred and intentionally omitted from display and search.

  Version: 0.1.0
  Status: Phase 2 Gate 2 Candidate

  Change Log:
    - 2026-07-02 - v0.1.0:
      - Add Customer List UI.
      - Wire search, filter, sort, and pagination to backend Customer List endpoint.
      - Omit AQX-C column and AQX-C search per Phase 2 override.
*/

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../../../utils/api";
import "./CustomersPage.css";

type CustomerStatus = "active" | "future_booking" | "new" | "lapsed" | string;

type CustomerRow = {
  name?: {
    first_name?: string | null;
    last_name?: string | null;
    preferred_name?: string | null;
    display_name?: string | null;
  };
  contact?: {
    email?: string | null;
    phone?: string | null;
  };
  certification_summary?: {
    level?: string | null;
    agency?: string | null;
  };
  last_dive_at?: string | null;
  booking_count?: number | string | null;
  completed_dive_count?: number | string | null;
  other_operator_activity_count?: number | string | null;
  groups?: Array<string | { name?: string | null; group_name?: string | null }>;
  status?: CustomerStatus | null;
  medical_status?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  guest_id?: string | number | null;
};

type CustomersResponse = {
  ok?: boolean;
  status?: string;
  data?: {
    customers?: CustomerRow[];
    pagination?: {
      total_count?: number;
      limit?: number;
      offset?: number;
      returned_count?: number;
    };
    filters?: {
      q?: string;
      status?: string;
      sort?: string;
      direction?: string;
    };
  };
  error?: string;
  message?: string;
};

type LoadState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; data: CustomersResponse };

const PAGE_LIMIT = 25;

function displayName(customer: CustomerRow): string {
  return (
    `${customer.name?.first_name || ""} ${customer.name?.last_name || ""}`.trim() ||
    customer.name?.display_name ||
    "Unnamed customer"
  );
}

function initials(name: string): string {
  return (
    name
      .split(" ")
      .filter(Boolean)
      .map((part) => part[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "CU"
  );
}

function formatPhone(phone?: string | null): string {
  return phone || "—";
}

function formatCertification(customer: CustomerRow): string {
  const level = customer.certification_summary?.level;
  const agency = customer.certification_summary?.agency;
  if (level && agency) return `${agency} ${level}`;
  if (level) return level;
  if (agency) return agency;
  return "Not recorded";
}

function relativeDate(value?: string | null): string {
  if (!value) return "No dives yet";

  const then = new Date(value);
  if (Number.isNaN(then.getTime())) return "Unknown";

  const now = new Date();
  const diffMs = now.getTime() - then.getTime();
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffDays < 0) return "Future";
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 30) return `${diffDays} days ago`;

  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths === 1) return "1 month ago";
  if (diffMonths < 12) return `${diffMonths} months ago`;

  const diffYears = Math.floor(diffMonths / 12);
  return diffYears === 1 ? "1 year ago" : `${diffYears} years ago`;
}

function normalizeStatus(status?: CustomerStatus | null): string {
  return String(status || "new").replace(/_/g, " ");
}

function statusClass(status?: CustomerStatus | null): string {
  const normalized = String(status || "new").toLowerCase();
  if (normalized === "active") return "customer-status customer-status-active";
  if (normalized === "future_booking") return "customer-status customer-status-future";
  if (normalized === "lapsed") return "customer-status customer-status-lapsed";
  return "customer-status customer-status-new";
}

function groupNames(customer: CustomerRow): string[] {
  return (customer.groups || [])
    .map((group) => {
      if (typeof group === "string") return group;
      return group.name || group.group_name || "";
    })
    .filter(Boolean);
}

export default function CustomersPage() {
  const nav = useNavigate();

  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sort, setSort] = useState("name");
  const [direction, setDirection] = useState("asc");
  const [offset, setOffset] = useState(0);
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [refreshKey, setRefreshKey] = useState(0);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);

  const endpoint = useMemo(() => {
    const params = new URLSearchParams();
    params.set("limit", String(PAGE_LIMIT));
    params.set("offset", String(offset));
    params.set("sort", sort);
    params.set("direction", direction);
    params.set("status", statusFilter);
    if (query.trim()) params.set("q", query.trim());
    return `/api/v1/dashboard/customers?${params.toString()}`;
  }, [query, statusFilter, sort, direction, offset]);

  useEffect(() => {
    let mounted = true;

    async function loadCustomers() {
      setState({ status: "loading" });
      try {
        const res = await apiFetch(endpoint, { method: "GET" });
        const json = (await res.json()) as CustomersResponse;

        if (!res.ok || json.ok === false) {
          throw new Error(json.error || json.message || `Customers load failed (${res.status})`);
        }

        if (mounted) setState({ status: "ready", data: json });
      } catch (err: any) {
        if (mounted) {
          setState({
            status: "error",
            message: err?.message || "Customers load failed"
          });
        }
      }
    }

    void loadCustomers();

    return () => {
      mounted = false;
    };
  }, [endpoint, refreshKey]);

  const customers = state.status === "ready" ? state.data.data?.customers || [] : [];
  const pagination = state.status === "ready" ? state.data.data?.pagination : undefined;
  const totalCount = pagination?.total_count || 0;
  const returnedCount = pagination?.returned_count || customers.length;
  const canPrevious = offset > 0;
  const canNext = offset + PAGE_LIMIT < totalCount;

  function resetAndSetQuery(value: string) {
    setQuery(value);
    setOffset(0);
  }

  function resetAndSetStatus(value: string) {
    setStatusFilter(value);
    setOffset(0);
  }

  function resetAndSetSort(value: string) {
    setSort(value);
    setOffset(0);
  }

  function toggleDirection() {
    setDirection((current) => (current === "asc" ? "desc" : "asc"));
    setOffset(0);
  }

  function refreshCustomers() {
    setOffset(0);
    setRefreshKey((value) => value + 1);
  }

  return (
    <div className="customers-page">
      <section className="customers-crown">
        <div>
          <div className="customers-kicker">Operations</div>
          <h1 className="customers-title">Customers</h1>
          <p className="customers-subtitle">
            The person axis of AQUORIX: profiles, bookings, groups, and dive history for this operator.
          </p>
        </div>
        <div className="customers-crown-actions">
          <button className="customers-import-button" type="button" onClick={() => setImportModalOpen(true)}>
            Import customers
          </button>
          <button className="customers-add-button" type="button" onClick={() => setAddModalOpen(true)}>
            + Add customer
          </button>
          <div className="customers-total-chip">
            <span>Total</span>
            <strong>{totalCount}</strong>
          </div>
        </div>
      </section>

      <section className="customers-controls" aria-label="Customer filters">
        <div className="customers-search-wrap">
          <label htmlFor="customer-search">Search</label>
          <input
            id="customer-search"
            value={query}
            onChange={(event) => resetAndSetQuery(event.target.value)}
            placeholder="Search name, phone, email, or group"
          />
        </div>

        <div className="customers-filter-wrap">
          <label htmlFor="customer-status">Status</label>
          <select
            id="customer-status"
            value={statusFilter}
            onChange={(event) => resetAndSetStatus(event.target.value)}
          >
            <option value="all">All customers</option>
            <option value="active">Active</option>
            <option value="future_booking">Future booking</option>
            <option value="new">New</option>
            <option value="lapsed">Lapsed</option>
          </select>
        </div>

        <div className="customers-filter-wrap">
          <label htmlFor="customer-sort">Sort</label>
          <select
            id="customer-sort"
            value={sort}
            onChange={(event) => resetAndSetSort(event.target.value)}
          >
            <option value="name">Name</option>
            <option value="last_dive">Last dive</option>
            <option value="booking_count">Bookings</option>
            <option value="status">Status</option>
          </select>
        </div>

        <button className="customers-sort-direction" type="button" onClick={toggleDirection}>
          {direction === "asc" ? "Ascending" : "Descending"}
        </button>
      </section>

      {state.status === "error" ? (
        <section className="customers-state customers-state-error">
          <strong>Customers load failed.</strong>
          <span>{state.message}</span>
        </section>
      ) : null}

      <section className="customers-table-card">
        <div className="customers-table-scroll">
          <table className="customers-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Phone</th>
                <th>Certification</th>
                <th>Last dive</th>
                <th>Bookings</th>
                <th>Groups</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {state.status === "loading" ? (
                <tr>
                  <td colSpan={7} className="customers-empty-cell">Loading customers...</td>
                </tr>
              ) : customers.length === 0 ? (
                <tr>
                  <td colSpan={7} className="customers-empty-cell">No customers found.</td>
                </tr>
              ) : (
                customers.map((customer, index) => {
                  const name = displayName(customer);
                  const groups = groupNames(customer);
                  const visibleGroups = groups.slice(0, 2);
                  const extraGroups = groups.length - visibleGroups.length;
                  const detailKey = customer.guest_id || `${name}-${index}`;

                  return (
                    <tr
                      key={detailKey}
                      className="customers-row"
                      onClick={() => nav(`/dashboard/customers/${encodeURIComponent(String(customer.guest_id || ""))}`)}
                    >
                      <td>
                        <div className="customers-name-cell">
                          <div className="customers-avatar">{initials(name)}</div>
                          <div>
                            <div className="customers-name">{name}</div>
                            <div className="customers-email">{customer.contact?.email || "No email recorded"}</div>
                          </div>
                        </div>
                      </td>
                      <td>{formatPhone(customer.contact?.phone)}</td>
                      <td>{formatCertification(customer)}</td>
                      <td>{relativeDate(customer.last_dive_at)}</td>
                      <td>
                        <span className="customers-count">{customer.booking_count ?? 0}</span>
                      </td>
                      <td>
                        <div className="customers-groups">
                          {visibleGroups.length > 0 ? (
                            visibleGroups.map((group) => (
                              <span className="customers-group-chip" key={group}>{group}</span>
                            ))
                          ) : (
                            <span className="customers-muted">None</span>
                          )}
                          {extraGroups > 0 ? <span className="customers-group-more">+{extraGroups}</span> : null}
                        </div>
                      </td>
                      <td>
                        <span className={statusClass(customer.status)}>{normalizeStatus(customer.status)}</span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="customers-pagination">
          <div>
            Showing {returnedCount} of {totalCount}
          </div>
          <div className="customers-pagination-actions">
            <button type="button" disabled={!canPrevious} onClick={() => setOffset(Math.max(0, offset - PAGE_LIMIT))}>
              Previous
            </button>
            <button type="button" disabled={!canNext} onClick={() => setOffset(offset + PAGE_LIMIT)}>
              Next
            </button>
          </div>
        </div>
      </section>

      {importModalOpen ? (
        <CustomerImportModal
          onClose={() => setImportModalOpen(false)}
          onImported={refreshCustomers}
        />
      ) : null}

      {addModalOpen ? (
        <AddCustomerModal
          onClose={() => setAddModalOpen(false)}
          onCreated={() => {
            setAddModalOpen(false);
            refreshCustomers();
          }}
        />
      ) : null}
    </div>
  );
}



type CustomerImportSummary = {
  total: number;
  create: number;
  update: number;
  needs_review: number;
  invalid: number;
  created?: number;
  updated?: number;
  skipped?: number;
};

type CustomerImportPreviewRow = {
  row_number: number;
  action: string;
  status: string;
  message: string;
  input?: Record<string, any>;
  match?: {
    guest_id?: string;
    display_name?: string;
    reason?: string;
  } | null;
};

type CustomerImportResponse = {
  ok?: boolean;
  status?: string;
  data?: {
    rows?: CustomerImportPreviewRow[];
    summary?: CustomerImportSummary;
  };
  message?: string;
};

const CUSTOMER_IMPORT_COLUMNS = [
  "first_name",
  "last_name",
  "preferred_name",
  "email",
  "phone",
  "date_of_birth",
  "nationality_code",
  "gender",
  "id_document_type",
  "id_document_number",
  "certifications",
  "medical_readiness_status"
];

const CUSTOMER_IMPORT_TEMPLATE_ROW = [
  "Sam",
  "Customer",
  "Sam",
  "sam@example.com",
  "+12075551234",
  "1984-05-20",
  "US",
  "male",
  "passport",
  "P1234567",
  "SSI Open Water",
  "not_collected"
];

function parseCsvLine(line: string): string[] {
  const values: string[] = [];
  let current = "";
  let quoted = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    const next = line[i + 1];

    if (char === '"' && quoted && next === '"') {
      current += '"';
      i += 1;
    } else if (char === '"') {
      quoted = !quoted;
    } else if (char === "," && !quoted) {
      values.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }

  values.push(current.trim());
  return values;
}

function parseCustomerCsv(text: string): Record<string, string>[] {
  const lines = text
    .replace(/\r/g, "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length < 2) {
    throw new Error("CSV needs a header row and at least one customer row.");
  }

  const headerIndex = lines.findIndex((line) => {
    const candidateHeaders = parseCsvLine(line).map((header) => header.trim().replace(/^\uFEFF/, ""));
    return CUSTOMER_IMPORT_COLUMNS.every((column) => candidateHeaders.includes(column));
  });

  if (headerIndex < 0) {
    throw new Error(`Missing template header row. Required columns: ${CUSTOMER_IMPORT_COLUMNS.join(", ")}`);
  }

  const headers = parseCsvLine(lines[headerIndex]).map((header) => header.trim().replace(/^\uFEFF/, ""));

  return lines.slice(headerIndex + 1).map((line) => {
    const values = parseCsvLine(line);
    const row: Record<string, string> = {};
    headers.forEach((header, index) => {
      row[header] = values[index] || "";
    });
    return row;
  }).filter((row) => Object.values(row).some((value) => String(value || "").trim()));
}

function downloadCustomerImportTemplate() {
  const csv = [
    CUSTOMER_IMPORT_COLUMNS.join(","),
    CUSTOMER_IMPORT_TEMPLATE_ROW.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(",")
  ].join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = "aquorix-customer-import-template.csv";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function CustomerImportModal({
  onClose,
  onImported
}: {
  onClose: () => void;
  onImported: () => void;
}) {
  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [preview, setPreview] = useState<CustomerImportResponse["data"] | null>(null);
  const [result, setResult] = useState<CustomerImportResponse["data"] | null>(null);
  const [message, setMessage] = useState("");
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  async function previewFile(file: File | null) {
    setMessage("");
    setPreview(null);
    setResult(null);
    setRows([]);
    setFileName(file?.name || "");

    if (!file) return;

    setIsPreviewing(true);

    try {
      const parsedRows = parseCustomerCsv(await file.text());
      setRows(parsedRows);

      const res = await apiFetch("/api/v1/dashboard/customers/import/preview", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rows: parsedRows })
      });

      const json: CustomerImportResponse = await res.json();
      if (!res.ok || json.ok === false) {
        throw new Error(json.message || `Import preview failed (${res.status})`);
      }

      setPreview(json.data || null);
    } catch (err: any) {
      setMessage(err?.message || "Import preview failed.");
    } finally {
      setIsPreviewing(false);
    }
  }

  async function importCustomers() {
    if (!rows.length || isImporting) return;

    setMessage("");
    setIsImporting(true);

    try {
      const res = await apiFetch("/api/v1/dashboard/customers/import/commit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rows })
      });

      const json: CustomerImportResponse = await res.json();
      if (!res.ok || json.ok === false) {
        throw new Error(json.message || `Customer import failed (${res.status})`);
      }

      setResult(json.data || null);
      onImported();
    } catch (err: any) {
      setMessage(err?.message || "Customer import failed.");
    } finally {
      setIsImporting(false);
    }
  }

  const summary = result?.summary || preview?.summary || null;
  const importableCount = summary ? summary.create + summary.update : 0;
  const canImport = Boolean(preview && importableCount > 0 && !result);

  return (
    <div className="customers-modal-backdrop">
      <div className="customers-add-modal customer-import-modal" role="dialog" aria-modal="true" aria-label="Import customers">
        <div className="customers-add-head">
          <div className="customers-add-avatar">⇧</div>
          <div>
            <h2>Import customers</h2>
            <p>Import customer identity and certification fields from a CSV file.</p>
          </div>
          <button className="customers-modal-close" type="button" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="customers-add-body">
          <section className="customers-form-panel">
            <div className="customers-form-section-title">CSV file</div>
            <p className="customers-import-help">
              Use the AQUORIX template columns. Blank optional fields are OK. This does not create bookings, sessions, forms, or payments.
            </p>
            <div className="customers-import-key" aria-label="CSV import field guidance">
              <div><strong>Export:</strong> CSV only. Do not include table names.</div>
              <div><strong>Document type:</strong> passport, national_id, or blank.</div>
              <div><strong>Gender:</strong> male, female, or blank.</div>
              <div><strong>Medical readiness:</strong> not_collected, no_conditions_declared, declared_cleared_by_physician, declared_awaiting_clearance, needs_review, or blank.</div>
            </div>
            <div className="customers-import-actions">
              <button className="customers-cancel-button" type="button" onClick={downloadCustomerImportTemplate}>
                Download template
              </button>
              <label className="customers-import-file">
                <span>{fileName || "Choose CSV file"}</span>
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(event) => previewFile(event.target.files?.[0] || null)}
                />
              </label>
            </div>
          </section>

          {message ? <div className="customers-add-error">{message}</div> : null}

          {summary ? (
            <section className="customers-form-panel">
              <div className="customers-form-section-title">{result ? "Import result" : "Import preview"}</div>
              <div className="customers-import-summary">
                <div><span>Total</span><strong>{summary.total}</strong></div>
                <div><span>Create</span><strong>{summary.create}</strong></div>
                <div><span>Update</span><strong>{summary.update}</strong></div>
                <div><span>Review</span><strong>{summary.needs_review}</strong></div>
                <div><span>Invalid</span><strong>{summary.invalid}</strong></div>
              </div>
            </section>
          ) : null}

          {(result?.rows || preview?.rows || []).length ? (
            <section className="customers-form-panel">
              <div className="customers-form-section-title">Rows</div>
              <div className="customers-import-rows">
                {(result?.rows || preview?.rows || []).slice(0, 12).map((row) => (
                  <div className={`customers-import-row customers-import-row-${row.status}`} key={`${row.row_number}-${row.action}`}>
                    <strong>Row {row.row_number}</strong>
                    <span>{row.action}</span>
                    <p>{row.message}</p>
                  </div>
                ))}
              </div>
            </section>
          ) : null}
        </div>

        <div className="customers-add-foot">
          <button className="customers-cancel-button" type="button" onClick={onClose}>Close</button>
          <button className="customers-save-button" type="button" onClick={importCustomers} disabled={!canImport || isPreviewing || isImporting}>
            {isPreviewing ? "Previewing..." : isImporting ? "Importing..." : result ? "Imported" : `Import ${importableCount || ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}


type AddCustomerForm = {
  first_name: string;
  last_name: string;
  preferred_name: string;
  email: string;
  phone: string;
  date_of_birth: string;
  nationality_code: string;
  gender: string;
  id_document_type: string;
  id_document_number: string;
  certification_agency: string;
  certification_level: string;
  certification_number: string;
};

const EMPTY_ADD_CUSTOMER_FORM: AddCustomerForm = {
  first_name: "",
  last_name: "",
  preferred_name: "",
  email: "",
  phone: "",
  date_of_birth: "",
  nationality_code: "",
  gender: "",
  id_document_type: "",
  id_document_number: "",
  certification_agency: "",
  certification_level: "",
  certification_number: ""
};

function AddCustomerModal({
  onClose,
  onCreated
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [form, setForm] = useState<AddCustomerForm>(EMPTY_ADD_CUSTOMER_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState("");

  function setField(field: keyof AddCustomerForm, value: string) {
    setForm((current) => ({ ...current, [field]: value }));
    setMessage("");
  }

  async function saveCustomer() {
    if (isSaving) return;

    const payload = {
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      preferred_name: form.preferred_name.trim() || null,
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      date_of_birth: form.date_of_birth || null,
      nationality_code: form.nationality_code.trim().toUpperCase().slice(0, 2) || null,
      gender: form.gender || null,
      id_document_type: form.id_document_type || null,
      id_document_number: form.id_document_number.trim() || null,
      certification_agency: form.certification_agency.trim() || null,
      certification_level: form.certification_level.trim() || null,
      certification_number: form.certification_number.trim() || null
    };

    if (!payload.first_name || !payload.last_name) {
      setMessage("First and last name are required.");
      return;
    }

    if (!payload.email && !payload.phone) {
      setMessage("Add either email or phone.");
      return;
    }

    if (form.nationality_code.trim() && form.nationality_code.trim().length !== 2) {
      setMessage("Use the 2-letter nationality code, for example US, EG, or JO.");
      return;
    }

    setIsSaving(true);

    try {
      const res = await apiFetch("/api/v1/dashboard/customers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      const json = await res.json();

      if (!res.ok || json.ok === false) {
        throw new Error(json.message || `Customer could not be created (${res.status})`);
      }

      onCreated();
    } catch (err: any) {
      setMessage(err?.message || "Customer could not be created.");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="customers-modal-backdrop">
      <div className="customers-add-modal" role="dialog" aria-modal="true" aria-label="Add customer">
        <div className="customers-add-head">
          <div className="customers-add-avatar">+</div>
          <div>
            <h2>Add customer</h2>
            <p>Create an operator customer profile with the identity fields used by bookings and manifest.</p>
          </div>
          <button className="customers-modal-close" type="button" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="customers-add-body">
          <section className="customers-form-panel">
            <div className="customers-form-section-title">Identity</div>
            <div className="customers-form-grid">
              <label className="customers-field">
                <span>First name</span>
                <input value={form.first_name} onChange={(event) => setField("first_name", event.target.value)} />
              </label>
              <label className="customers-field">
                <span>Last name</span>
                <input value={form.last_name} onChange={(event) => setField("last_name", event.target.value)} />
              </label>
              <label className="customers-field">
                <span>Preferred name</span>
                <input value={form.preferred_name} onChange={(event) => setField("preferred_name", event.target.value)} />
              </label>
              <label className="customers-field">
                <span>Date of birth</span>
                <input type="date" value={form.date_of_birth} onChange={(event) => setField("date_of_birth", event.target.value)} />
              </label>
              <label className="customers-field">
                <span>Nationality code</span>
                <input value={form.nationality_code} onChange={(event) => setField("nationality_code", event.target.value)} placeholder="US" maxLength={2} />
              </label>
              <label className="customers-field">
                <span>Gender</span>
                <select value={form.gender} onChange={(event) => setField("gender", event.target.value)}>
                  <option value="">Not recorded</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </label>
            </div>
          </section>

          <section className="customers-form-panel">
            <div className="customers-form-section-title">Contact & document</div>
            <div className="customers-form-grid">
              <label className="customers-field">
                <span>Email</span>
                <input type="email" value={form.email} onChange={(event) => setField("email", event.target.value)} />
              </label>
              <label className="customers-field">
                <span>Phone</span>
                <input value={form.phone} onChange={(event) => setField("phone", event.target.value)} />
              </label>
              <label className="customers-field">
                <span>Document type</span>
                <select value={form.id_document_type} onChange={(event) => setField("id_document_type", event.target.value)}>
                  <option value="">Not recorded</option>
                  <option value="passport">Passport</option>
                  <option value="national_id">National ID</option>
                </select>
              </label>
              <label className="customers-field">
                <span>Document number</span>
                <input value={form.id_document_number} onChange={(event) => setField("id_document_number", event.target.value)} />
              </label>
            </div>
          </section>

          <section className="customers-form-panel">
            <div className="customers-form-section-title">Certification</div>
            <div className="customers-form-grid">
              <label className="customers-field">
                <span>Agency</span>
                <input value={form.certification_agency} onChange={(event) => setField("certification_agency", event.target.value)} placeholder="PADI" />
              </label>
              <label className="customers-field">
                <span>Level</span>
                <input value={form.certification_level} onChange={(event) => setField("certification_level", event.target.value)} placeholder="Open Water" />
              </label>
              <label className="customers-field">
                <span>Certification number</span>
                <input value={form.certification_number} onChange={(event) => setField("certification_number", event.target.value)} />
              </label>
            </div>
          </section>

          {message ? <div className="customers-add-error">{message}</div> : null}
        </div>

        <div className="customers-add-foot">
          <button className="customers-cancel-button" type="button" onClick={onClose}>Cancel</button>
          <button className="customers-save-button" type="button" onClick={saveCustomer} disabled={isSaving}>
            {isSaving ? "Saving..." : "Add customer"}
          </button>
        </div>
      </div>
    </div>
  );
}
